import { Head, router } from "@inertiajs/react"
import {
  Users,
  Crown,
  Clock,
  UserPlus,
  Search,
  MoreHorizontal,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface Cupper {
  id: number
  name: string
  email: string
  created_at: string
  admin: boolean
}

interface PendingInvitation {
  id: number
  email: string
  created_at: string
  expires_at: string
}

interface Stats {
  members: number
  admins: number
  pending_invites: number
  seats_available: number
}

interface Organization {
  id: number
  name: string
  slug: string
}

interface Props {
  cuppers: Cupper[]
  pending_invitations: PendingInvitation[]
  stats: Stats
  organization: Organization
}

export default function CuppersIndex({
  cuppers,
  pending_invitations,
  stats,
  organization,
}: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("cuppers.breadcrumb"),
      href: "/cuppers",
    },
  ]

  const filteredCuppers = cuppers.filter(
    (cupper) =>
      cupper.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cupper.email.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return

    router.post(
      "/cuppers",
      { email: inviteEmail },
      {
        onSuccess: () => {
          setInviteModalOpen(false)
          setInviteEmail("")
        },
      },
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return (
      date.toLocaleDateString("es", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }) +
      " | " +
      date.toLocaleTimeString("es", {
        hour: "2-digit",
        minute: "2-digit",
      })
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("cuppers.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <div>
            <p className="text-muted-foreground text-sm">{organization.name}</p>
            <h1 className="text-2xl font-bold">{t("cuppers.manage_title")}</h1>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={() => setInviteModalOpen(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {t("cuppers.invite_member")}
            </Button>
            <Button
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
            >
              {t("cuppers.upgrade_plan")}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Users className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.members}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("cuppers.stats.members")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Crown className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.admins}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("cuppers.stats.admins")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Clock className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.pending_invites}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("cuppers.stats.pending_invites")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <UserPlus className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.seats_available}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("cuppers.stats.seats_available")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={t("cuppers.search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cuppers.table.name")}</TableHead>
                  <TableHead className="text-center">
                    Puede crear sesión
                  </TableHead>
                  <TableHead className="text-center">Admin</TableHead>
                  <TableHead className="text-center">Activo</TableHead>
                  <TableHead>{t("cuppers.table.joined")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCuppers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground py-8 text-center"
                    >
                      {searchQuery
                        ? `${t("common.no_results")}`
                        : t("cuppers.no_cuppers")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCuppers.map((cupper) => (
                    <TableRow key={cupper.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full font-medium">
                            {cupper.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{cupper.name}</p>
                            <p className="text-muted-foreground text-sm">
                              {cupper.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox checked={true} disabled />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox checked={cupper.admin || false} disabled />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox checked={true} disabled />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(cupper.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>Ver perfil</DropdownMenuItem>
                            <DropdownMenuItem>Editar permisos</DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                if (confirm(t("common.confirm"))) {
                                  router.delete(`/cuppers/${cupper.id}`)
                                }
                              }}
                            >
                              {t("common.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {pending_invitations.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">
                {t("cuppers.pending_invitations.title")}
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("cuppers.pending_invitations.email")}
                    </TableHead>
                    <TableHead>
                      {t("cuppers.pending_invitations.sent")}
                    </TableHead>
                    <TableHead>
                      {t("cuppers.pending_invitations.expires")}
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending_invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">
                        {invitation.email}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(invitation.created_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(invitation.expires_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (confirm(t("common.confirm"))) {
                              router.delete(`/invitations/${invitation.id}`)
                            }
                          }}
                        >
                          {t("cuppers.pending_invitations.cancel")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="icon" disabled>
            &lt;
          </Button>
          <Button variant="default" size="sm" className="bg-primary">
            1
          </Button>
          <Button variant="ghost" size="icon" disabled>
            &gt;
          </Button>
        </div>

        {/* Invite Modal */}
        <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("cuppers.invite_modal.title")}</DialogTitle>
              <DialogDescription>
                {t("cuppers.invite_modal.description")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t("cuppers.invite_modal.email_label")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("cuppers.invite_modal.email_placeholder")}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="border-primary"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setInviteModalOpen(false)}
                >
                  {t("cuppers.invite_modal.cancel")}
                </Button>
                <Button type="submit" disabled={!inviteEmail.trim()}>
                  {t("cuppers.invite_modal.send_invite")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  )
}
